import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, Facebook, Instagram } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { CITIES, SERVICE_LINKS } from "@/seo/seoConfig";

// TikTok icon component
const TikTok = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-b from-[#0F172A] to-[#1E293B] text-white" data-testid="footer">
      <div className="px-4 md:px-8 lg:px-16 py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 mb-8">
          {/* Logo Section */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_4c5b4c59-adba-4a3b-bcab-ae105abc8417/artifacts/36mrxgy3_ChatGPT%20Image%2016%20ene%202026%2C%2002_31_55%20p.m..png" 
                alt="Pellon Morel Solutions"
                className="h-14 w-auto"
              />
              <div className="flex flex-col items-center">
                <h3 className="text-base font-bold text-white leading-none tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Pellon Morel</h3>
                <div className="h-[1.5px] bg-[#D4AF37] w-full my-[2px]"></div>
                <p className="text-xs font-semibold text-white leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>Solutions</p>
                <p className="text-[7px] text-[#D4AF37] uppercase tracking-wider font-medium leading-none mt-[1px]">Plumbing-Drain-Repairs</p>
              </div>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t.footer.description}
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{t.footer.servicesTitle}</h4>
            <ul className="space-y-2 text-slate-300 text-sm">
              {SERVICE_LINKS.map((s) => (
                <li key={s.slug}>
                  <Link to={`/${s.slug}`} className="hover:text-[#D4AF37] transition-colors" data-testid={`footer-service-link-${s.slug}`}>{s[language]}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{t.footer.contactTitle}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <Phone className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{language === 'en' ? 'Call' : 'Llamar'}</p>
                  <a href="tel:+17576503192" className="hover:text-[#D4AF37] transition-colors">(757) 650-3192</a>
                </div>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <Mail className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <a href="mailto:info@pellonmorel.com" className="hover:text-[#D4AF37] transition-colors">info@pellonmorel.com</a>
              </li>
              <li className="flex items-start gap-2 text-slate-300 text-sm">
                <MapPin className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p>{t.footer.locationText}</p>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-base font-semibold mb-4 text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{t.footer.hoursTitle}</h4>
            <div className="space-y-3 text-slate-300 text-sm">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-[#D4AF37]">{t.footer.emergency}</p>
                  <p className="text-xs">{t.footer.emergencyText}</p>
                </div>
              </div>
              <div className="text-xs">
                <p className="font-medium mb-1">{t.footer.regularHours}</p>
                <p>{t.footer.weekdays}</p>
                <p>{t.footer.saturday}</p>
                <p>{t.footer.sunday}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Service Areas */}
        <div className="border-t border-slate-700 pt-6 mb-6" data-testid="footer-service-areas">
          <p className="text-xs uppercase tracking-widest text-[#D4AF37] font-semibold mb-3">{language === 'en' ? 'Service Areas in Hampton Roads, VA' : 'Áreas de Servicio en Hampton Roads, VA'}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-300">
            {CITIES.map((c) => (
              <Link key={c.slug} to={`/${c.slug}`} className="hover:text-[#D4AF37] transition-colors" data-testid={`footer-city-link-${c.slug}`}>
                {language === 'en' ? `Plumber in ${c.name}, VA` : `Plomero en ${c.name}, VA`}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-xs">
              © {currentYear} Pellon Morel Solutions. All Rights Reserved.
            </p>
            <div className="flex items-center gap-4">
              {/* Social Icons - Only Facebook, Instagram, TikTok */}
              <div className="flex gap-3">
                <a href="#" className="w-9 h-9 bg-slate-800 hover:bg-[#1877F2] rounded-full flex items-center justify-center transition-colors" aria-label="Facebook">
                  <Facebook className="w-4 h-4" />
                </a>
                <a href="#" className="w-9 h-9 bg-slate-800 hover:bg-[#E4405F] rounded-full flex items-center justify-center transition-colors" aria-label="Instagram">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="#" className="w-9 h-9 bg-slate-800 hover:bg-black rounded-full flex items-center justify-center transition-colors" aria-label="TikTok">
                  <TikTok className="w-4 h-4" />
                </a>
              </div>
              <a
                href="https://app.emergent.sh/?utm_source=emergent-badge"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-slate-500 hover:text-white transition-colors text-[10px]"
              >
                <img 
                  src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
                  alt="Emergent"
                  className="w-3.5 h-3.5"
                />
                <span>Made with Emergent</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;